import followUpCadenceDefinitionsAPI from '../../api/followUpCadenceDefinitions';
import followUpCadenceRunsAPI from '../../api/followUpCadenceRuns';
import followUpCadenceMediaAssetsAPI from '../../api/followUpCadenceMediaAssets';

const state = {
  definitions: [],
  runs: [],
  mediaAssets: [],
  uiFlags: {
    isFetchingDefinitions: false,
    isFetchingRuns: false,
    isFetchingMedia: false,
    isSavingDefinition: false,
    isUploadingMedia: false,
  },
};

const getters = {
  getDefinitions: $state => $state.definitions,
  getDefinition: $state => id => $state.definitions.find(d => d.id === id),
  getRuns: $state => $state.runs,
  getMediaAssets: $state => $state.mediaAssets,
  getUIFlags: $state => $state.uiFlags,
};

const actions = {
  async fetchDefinitions({ commit }) {
    commit('SET_UI_FLAG', { isFetchingDefinitions: true });
    try {
      const response = await followUpCadenceDefinitionsAPI.get();
      commit('SET_DEFINITIONS', response.data);
    } finally {
      commit('SET_UI_FLAG', { isFetchingDefinitions: false });
    }
  },

  async createDefinition({ commit }, data) {
    commit('SET_UI_FLAG', { isSavingDefinition: true });
    try {
      const response = await followUpCadenceDefinitionsAPI.create(data);
      commit('ADD_DEFINITION', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isSavingDefinition: false });
    }
  },

  async updateDefinition({ commit }, { id, ...data }) {
    commit('SET_UI_FLAG', { isSavingDefinition: true });
    try {
      const response = await followUpCadenceDefinitionsAPI.update(id, data);
      commit('UPDATE_DEFINITION', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isSavingDefinition: false });
    }
  },

  async deleteDefinition({ commit }, id) {
    await followUpCadenceDefinitionsAPI.delete(id);
    commit('DELETE_DEFINITION', id);
  },

  async fetchRuns({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetchingRuns: true });
    try {
      const response = await followUpCadenceRunsAPI.get(params);
      commit('SET_RUNS', response.data);
    } finally {
      commit('SET_UI_FLAG', { isFetchingRuns: false });
    }
  },

  // advup — 2026-07-28: botão manual "Colocar em follow-up" na conversa.
  async createRun(
    { commit },
    { conversationId, followUpCadenceDefinitionId, startOn, mesmoAssim }
  ) {
    const response = await followUpCadenceRunsAPI.create({
      conversationId,
      followUpCadenceDefinitionId,
      startOn,
      mesmoAssim,
    });
    commit('ADD_RUN', response.data);
    return response.data;
  },

  async pauseRun({ commit }, id) {
    const response = await followUpCadenceRunsAPI.pause(id);
    commit('UPDATE_RUN', response.data);
  },

  async resumeRun({ commit }, id) {
    const response = await followUpCadenceRunsAPI.resume(id);
    commit('UPDATE_RUN', response.data);
  },

  async approveStep({ commit }, { id, content }) {
    let response;
    try {
      response = await followUpCadenceRunsAPI.approveStep(id, content);
    } catch (error) {
      // Não saiu pelo WhatsApp (22/10/2026): o servidor passa a conversa para
      // a equipe e devolve a execução como ficou — a linha sai de "aguardando".
      const run = error?.response?.data?.run;
      if (run) commit('UPDATE_RUN', run);
      throw error;
    }
    commit('UPDATE_RUN', response.data);
    // Devolve para a tela: a aprovação pode ter entrado na fila por causa do
    // intervalo mínimo, e aí vem `aviso` com o horário de saída.
    return response.data;
  },

  async rejectStep({ commit }, id) {
    const response = await followUpCadenceRunsAPI.rejectStep(id);
    commit('UPDATE_RUN', response.data);
  },

  async regenerateStep({ commit }, id) {
    const response = await followUpCadenceRunsAPI.regenerateStep(id);
    commit('UPDATE_RUN', response.data);
    return response.data;
  },

  async fetchMediaAssets({ commit }) {
    commit('SET_UI_FLAG', { isFetchingMedia: true });
    try {
      const response = await followUpCadenceMediaAssetsAPI.get();
      commit('SET_MEDIA_ASSETS', response.data);
    } finally {
      commit('SET_UI_FLAG', { isFetchingMedia: false });
    }
  },

  async uploadMediaAsset({ commit }, { name, kind, file }) {
    commit('SET_UI_FLAG', { isUploadingMedia: true });
    try {
      const formData = new FormData();
      formData.append('follow_up_cadence_media_asset[name]', name);
      formData.append('follow_up_cadence_media_asset[kind]', kind);
      formData.append('follow_up_cadence_media_asset[file]', file);
      const response = await followUpCadenceMediaAssetsAPI.create(formData);
      commit('ADD_MEDIA_ASSET', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isUploadingMedia: false });
    }
  },

  async deleteMediaAsset({ commit }, id) {
    await followUpCadenceMediaAssetsAPI.delete(id);
    commit('DELETE_MEDIA_ASSET', id);
  },
};

const mutations = {
  SET_DEFINITIONS($state, definitions) {
    $state.definitions = definitions;
  },
  ADD_DEFINITION($state, definition) {
    $state.definitions.unshift(definition);
  },
  UPDATE_DEFINITION($state, definition) {
    const index = $state.definitions.findIndex(d => d.id === definition.id);
    if (index !== -1) {
      $state.definitions.splice(index, 1, definition);
    }
  },
  DELETE_DEFINITION($state, id) {
    $state.definitions = $state.definitions.filter(d => d.id !== id);
  },
  SET_RUNS($state, runs) {
    $state.runs = runs;
  },
  UPDATE_RUN($state, run) {
    const index = $state.runs.findIndex(r => r.id === run.id);
    if (index !== -1) {
      $state.runs.splice(index, 1, run);
    }
  },
  ADD_RUN($state, run) {
    $state.runs.push(run);
  },
  SET_MEDIA_ASSETS($state, assets) {
    $state.mediaAssets = assets;
  },
  ADD_MEDIA_ASSET($state, asset) {
    $state.mediaAssets.unshift(asset);
  },
  DELETE_MEDIA_ASSET($state, id) {
    $state.mediaAssets = $state.mediaAssets.filter(a => a.id !== id);
  },
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
